import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { folderService, type Folder } from "@/services/folderService";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

const PRESET_COLORS = [
  "#3B82F6", // blue-500
  "#10B981", // emerald-500
  "#F59E0B", // amber-500
  "#EF4444", // red-500
  "#8B5CF6", // violet-500
  "#EC4899", // pink-500
  "#06B6D4", // cyan-500
  "#84CC16", // lime-500
  "#F97316", // orange-500
  "#64748B", // slate-500
];

interface EditFolderModalProps {
  folder: Folder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditFolderModal({
  folder,
  open,
  onOpenChange,
}: EditFolderModalProps) {
  const [name, setName] = React.useState(folder.name || "");
  const [color, setColor] = React.useState<string | null>(
    folder.folder_color || null,
  );
  const [isPrivate, setIsPrivate] = React.useState<boolean>(
    folder.is_private || false,
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    setName(folder.name || "");
    setColor(folder.folder_color || null);
    setIsPrivate(folder.is_private || false);
  }, [folder]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const updates: any = { name: name.trim(), is_private: isPrivate };
      updates.folder_color = color || null;
      const { error } = await folderService.updateFolder(folder.id, updates);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["folderTree"] });
      onOpenChange(false);
      toast({ title: "Folder updated" });
    },
    onError: (err: any) =>
      toast({
        title: "Update failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const canSave = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Folder</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Folder name"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Private</div>
              <div className="text-xs text-muted-foreground">
                Only you can see this folder
              </div>
            </div>
            <Switch
              checked={isPrivate}
              onCheckedChange={(v) => setIsPrivate(Boolean(v))}
            />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">Color</label>
            <div className="mt-2 grid grid-cols-10 gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  className={`h-6 w-6 rounded border ${color === c ? "ring-2 ring-offset-2 ring-blue-500" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  aria-label={`Select ${c}`}
                />
              ))}
              <button
                className={`h-6 w-6 rounded border flex items-center justify-center text-[10px] ${!color ? "ring-2 ring-offset-2 ring-blue-500" : ""}`}
                onClick={() => setColor(null)}
                title="No color"
              >
                ×
              </button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!canSave || saveMutation.isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
